import { Dropdown, IDropdownOption, IDropdownStyles, mergeStyles, PrimaryButton } from '@fluentui/react';
import * as React from 'react';
import { PageHeader } from '../../../../web/common/components/PageHeader';
import { getHelpLogger } from '../../../../web/utils/getHelpLogger';
import { captureContentUpdate, collectTelemetry } from '../../../../web/utils/oneDsUtil';
import { isGetHelpSourceApp, getUserContext } from '../../../../web/utils/statelessUtil';
import { getString } from '../../../../web/utils/stringLoader';
import { AlchemyThemeRoutingData } from '../../../models/apiModels';
import { SourceApps } from '../../../models/constants/constants';
import { AlchemyThemeRoutingNode, ThemeRoutingNodesMap } from '../models/alchemyThemeRoutingModels';
import { ExtensionAttributeKeys, FreelanceRequest } from '../models/requestModels';
import { getContactUsClassNames, getContactUsStyles, getGetHelpDropdownStyles, IContactUsStyles } from '../styles/contactUsStyles';
import { getChatModalityButtonStyles } from '../styles/modalityStyles';
import { IContactUsCurrentTheme } from '../styles/theme.model';

import { IStyledComponent, color } from '../styles/themesUI';
import { LogType, LogHint } from '../../../../web/models/emeraldLoggingRequest'; 

interface IAlchemyThemeRoutingthemeRoutingStrings {
    themeRoutingDataOtherOption: string;
}

interface AlchemyThemeRoutingProps {
    themeRoutingData: AlchemyThemeRoutingData;
    draftRequest: FreelanceRequest;
    dispatchFetchAvailableModalitiesV2(): void;
    updateDraftRequest: (properties: Partial<FreelanceRequest>) => void;
    showModalitiesStickyFooter: boolean;
    sourceApp: SourceApps;
    themeName: string;
}

type IAlchemyThemeRoutingProps = AlchemyThemeRoutingProps & IStyledComponent<IContactUsStyles>;

const AlchemyThemeRouting = (props: IAlchemyThemeRoutingProps) => {
  React.useEffect(() => {
    getHelpLogger.logTicketingPageLoaded('CategoryDropDownsPage');
  }, []);
  const handleOnChildSelected = React.useCallback((selectedNode: AlchemyThemeRoutingNode, nodeLevel: 'L1' | 'L2' | 'L3') => {
    const selectedNodeValue = selectedNode ? selectedNode.Value : null;
    const draftRequest: Partial<FreelanceRequest> = {
      ExtensionAttributes: {},
    };
    switch (nodeLevel) {
      case 'L1':
        draftRequest.ExtensionAttributes[ExtensionAttributeKeys.ThemeL1] = selectedNodeValue;
        draftRequest.ExtensionAttributes[ExtensionAttributeKeys.ThemeL2] = null;
        draftRequest.ExtensionAttributes[ExtensionAttributeKeys.ThemeL3] = null;
        break;
      case 'L2':
        draftRequest.ExtensionAttributes[ExtensionAttributeKeys.ThemeL2] = selectedNodeValue;
        draftRequest.ExtensionAttributes[ExtensionAttributeKeys.ThemeL3] = null;
        break;
      case 'L3':
        draftRequest.ExtensionAttributes[ExtensionAttributeKeys.ThemeL3] = selectedNodeValue;
        break;
      default:
        break;
    }
    getHelpLogger.logThemeDropDownSelectionChanged(nodeLevel, selectedNodeValue);
    props.updateDraftRequest(draftRequest);
  }, [props]);

  const handleRoutingCompleted = React.useCallback(() => {
    getHelpLogger.logThemeConfirmButtonClick(
      props.draftRequest.ExtensionAttributes[ExtensionAttributeKeys.ThemeL1],
      props.draftRequest.ExtensionAttributes[ExtensionAttributeKeys.ThemeL2],
      props.draftRequest.ExtensionAttributes[ExtensionAttributeKeys.ThemeL3],
      props.themeRoutingData.AlchemyTheme
      );
    collectTelemetry({
      ContentId: 'ConfirmTheme', Type: LogType.PageAction, Hint: LogHint.ConfirmTheme,
      Details: {
        'alchemyTheme': props.themeRoutingData.AlchemyTheme,
        'themeL1': props.draftRequest.ExtensionAttributes[ExtensionAttributeKeys.ThemeL1],
        'themeL2': props.draftRequest.ExtensionAttributes[ExtensionAttributeKeys.ThemeL2],
        'themeL3': props.draftRequest.ExtensionAttributes[ExtensionAttributeKeys.ThemeL3]
      }
    });
    props.dispatchFetchAvailableModalitiesV2();
  }, [props]);

  const rootNode = React.useMemo(() => {
    if (!props.themeRoutingData) {
      return null;
    }

    let root: AlchemyThemeRoutingNode = null;

    // Had to use this for IE compatibility
    props.themeRoutingData.Nodes.forEach(n => {
      if ((n.Level || '').toLowerCase() === 'l1') {
        root = n;
      }
    });
    return root;
  }, [props.themeRoutingData]);

  if (!rootNode) {
      return null;
  }

  const contactUsClassNames = getContactUsClassNames(props.styles, { theme: props.theme });

  return (
    <>
      <PageHeader title={getString('result_requestCreationMainTitle')} description={getString('result_requestCreationMainDescription')} theme={props.theme } />
      <div className={contactUsClassNames.panelBody}>
        <AlchemyThemeRoutingLevel
          showModalitiesStickyFooter={props.showModalitiesStickyFooter}
          key={rootNode.Id}
          node={rootNode}
          nodesMap={props.themeRoutingData.NodesMap}
          handleOnChildSelected={handleOnChildSelected}
          handleRoutingCompleted={handleRoutingCompleted}
          draftRequest={props.draftRequest}
          sourceApp={props.sourceApp}
          themeName={props.themeName}
          theme={props.theme}
        />
      </div>
    </>
  );
};

export default AlchemyThemeRouting;

interface AlchemyThemeRoutingLevelProps {
    node: AlchemyThemeRoutingNode;
    nodesMap: ThemeRoutingNodesMap;
    handleOnChildSelected(node: AlchemyThemeRoutingNode, level: string): void;
    handleRoutingCompleted(): void;
    draftRequest: FreelanceRequest;
    showModalitiesStickyFooter: boolean;
    sourceApp: SourceApps;
    themeName: string;
    theme: IContactUsCurrentTheme;
}

/**
 * This component represents each Level in the dropdowns data. A level contains the label and placeholder information, and contains children. The children will be rendered in the dropdown options.
 * Once a child is selected, the corresponding node is set as the "Next Node" and rendered in sequence.
 * @param props AlchemyThemeRoutingLevelProps
 * @returns
 */
const AlchemyThemeRoutingLevel = (props: AlchemyThemeRoutingLevelProps) => {
    const [nextNode, setNextNode] = React.useState<AlchemyThemeRoutingNode>(null);
    const [selectedNodeId, setSelectedNodeId] = React.useState<string>('');
    const handleOnDropdownOptionSelected = React.useCallback((nodeId: string) => {
        setSelectedNodeId(nodeId);

        // Get the node selected by the user
        const selectedNode = findNodeById(nodeId, props.nodesMap);
        let newNextNode: AlchemyThemeRoutingNode;

        // If the node exists and it has the "childrenPointer" property, the childrenPointer node is the next node
        if (selectedNode && selectedNode.ChildrenPointer) {
            newNextNode = findNodeById(selectedNode.ChildrenPointer, props.nodesMap);
        } else {
            newNextNode = selectedNode;
        }

        setNextNode(newNextNode);

        props.handleOnChildSelected(selectedNode, props.node.Level);
    }, [ setNextNode, props.node, props.nodesMap, props.handleOnChildSelected, findNodeById, setSelectedNodeId ]);

    // If the current node has no children, it means this is a leaf node, so the "Continue" button is displayed to the user
    if (!props.node.Children && !props.node.ChildrenPointer) {
        const freelancerequest = { ExtensionAttributes: props.draftRequest.ExtensionAttributes };
        const alchemyThemeRoutingConfirmButton = 'alchemyThemeRoutingConfirmButton';
        const chatModalityButtonStyles = getChatModalityButtonStyles({ theme: props.theme, withBorderRadius: isGetHelpSourceApp(props.sourceApp) });
        React.useEffect(() => {
 LogALchemyThemeRoutingConfirmButtonShowEvent(alchemyThemeRoutingConfirmButton);
});
        getHelpLogger.logThemeConfirmButtonDisplay(
          props.draftRequest.ExtensionAttributes[ExtensionAttributeKeys.ThemeL1],
          props.draftRequest.ExtensionAttributes[ExtensionAttributeKeys.ThemeL2],
          props.draftRequest.ExtensionAttributes[ExtensionAttributeKeys.ThemeL3],
          props.draftRequest.ExtensionAttributes[ExtensionAttributeKeys.Theme]);
        return (
            <div>
                <PrimaryButton
                    styles={chatModalityButtonStyles}
                    onClick={props.handleRoutingCompleted}
                    id={alchemyThemeRoutingConfirmButton}
                    data-bi-bhvr="CONTACT"
                    data-bi-area="modalitiesSection"
                    data-bi-formnm="maxFunnel"
                    data-bi-name={alchemyThemeRoutingConfirmButton}
                    data-bi-freelancerequest={JSON.stringify(freelancerequest)}
                >
                    {getString('modality_alchemyThemeRoutingConfirmButtonLabel')}
                </PrimaryButton>
            </div>
        );
    }

    return (
        <>
            {/* If the node has children, then a dropdown menu is displayed */}
            {!!props.node.Children && props.node.Level ? <AlchemyThemeRoutingDropdown showModalitiesStickyFooter={props.showModalitiesStickyFooter} selectedNodeId={selectedNodeId} parentId={props.node.Id} handleOnOptionSelected={handleOnDropdownOptionSelected} node={props.node} nodesMap={props.nodesMap} sourceApp={props.sourceApp} themeName={props.themeName} theme={props.theme} /> : null}

            {/* If there's a valid child selected, this child is rendered as the next level */}
            { nextNode ? (<AlchemyThemeRoutingLevel
                showModalitiesStickyFooter={props.showModalitiesStickyFooter}
                key={`${props.node.Label}-${props.node.Id}-${nextNode.Id}-${nextNode.Label}-${selectedNodeId}`}
                node={nextNode}
                nodesMap={props.nodesMap}
                handleOnChildSelected={props.handleOnChildSelected}
                handleRoutingCompleted={props.handleRoutingCompleted}
                draftRequest={props.draftRequest}
                sourceApp={props.sourceApp}
                themeName={props.themeName}
                theme={props.theme}
            />) : null}
        </>
    );
};

interface AlchemyThemeRoutingDropdownProps {
    parentId: string;
    selectedNodeId: string;
    node: AlchemyThemeRoutingNode;
    nodesMap: ThemeRoutingNodesMap;
    handleOnOptionSelected(nodeId: string): void;
    showModalitiesStickyFooter: boolean;
    sourceApp: SourceApps;
    themeName: string;
    theme: IContactUsCurrentTheme;
}

const LogALchemyThemeRoutingConfirmButtonShowEvent = (buttonId: string) => {
    const contentUpdateTags = {
        content: [{ contentId: buttonId, contentName: buttonId, formnm: 'maxFunnel', areaName: 'modalitiesSection' }],
        behavior: 'SHOW',
    };
    captureContentUpdate(contentUpdateTags);
};

const LogAlchemyThemeRoutingDropdownShowEvent = (props: AlchemyThemeRoutingDropdownProps) => {
    const contentId = props.parentId.startsWith('L0_') ? 'l1ThemeSelector' : props.parentId.startsWith('L2_') ? 'l2ThemeSelector' : 'l3ThemeSelector';
    const behavior = !props.selectedNodeId ? 'SHOW' : 'APPLY';
    if (behavior === 'SHOW') {
        const contentUpdateTags = {
            content: [{ contentId: contentId, contentName: contentId, formnm: 'maxFunnel', areaName: 'modalitiesSection', parentId: props.parentId, selectedNodeId: props.selectedNodeId }],
            behavior: behavior,
        };
        captureContentUpdate(contentUpdateTags);
    }
};

const AlchemyThemeRoutingDropdown = (props: AlchemyThemeRoutingDropdownProps) => {
    React.useEffect(() => {
        LogAlchemyThemeRoutingDropdownShowEvent(props);
    });

    /**
     * Since each child is just an ID, we need to populate the list with the actual nodes in order to get their labels and values
     */
    const nodeChildren = React.useMemo(() => {
        if (!props.node.Children) {
            return null;
        }
        return props.node.Children.map( nid => findNodeById(nid, props.nodesMap));
    }, [ props.node, props.nodesMap ]);

    if (!nodeChildren) {
        return null;
    }

    const [isFixed, setIsFixed] = React.useState<boolean>(false);
    const [isL3PrePopulated, setIsL3PrePopulated] = React.useState<boolean>(false);

    const getStickyFooterExperimentLabel = (id: string, label: string, props: AlchemyThemeRoutingDropdownProps) => {
        if (props.showModalitiesStickyFooter && id) {
            switch (id.toLowerCase()) {
                case 'L1_OtherProducts_L0_otherOrTP'.toLowerCase():
                    return getString('modality_themeRoutingDataOtherOption');
                default:
                    return label;
            }
        }
        return label;
    };

    const dropdownOptions: IDropdownOption[] = nodeChildren.map(
        (n, k) => {
            return {
                    key: `${k}-${n.Id}`,
                    value: n.Id,
                    selected: props.selectedNodeId === n.Id,
                    text: getStickyFooterExperimentLabel(n.Id, n.Label, props),
                    };
                });

    const getDropdownValueFromEventTarget = React.useCallback((_event: React.FormEvent<HTMLDivElement>, option: any) => {
        props.handleOnOptionSelected(!!option ? option.value : null);
    },[ props.handleOnOptionSelected ]);

    React.useEffect(() => {
        if ((props.sourceApp === SourceApps.SurfaceApp || props.sourceApp === SourceApps.SurfaceApp2 || props.sourceApp === SourceApps.SurfaceWeb) && props.node.Level === 'L1') {
            setIsFixed(true);
        } else {
            setIsFixed(false);
        }

        if ((props.sourceApp === SourceApps.SurfaceApp || props.sourceApp === SourceApps.SurfaceApp2) && props.node.Level === "L3" ) {
            setIsL3PrePopulated(true);
        } else {
            setIsL3PrePopulated(false);
        }
    });

    /**
     *  find l1_surface_id and pass it to handle function
     */
    React.useEffect(() => {
        if (isFixed && !!nodeChildren) {
            const surfaceArray = nodeChildren.filter(e => e.Id.toString().toLowerCase().indexOf('l1_surface') > -1);
            if (!!surfaceArray && surfaceArray.length > 0) {
                props.handleOnOptionSelected(surfaceArray[0].Id);
            }
        }
    }, [isFixed, nodeChildren]);

    /**
     *  find l3_surface_productid and pass it to handle function
     */
    React.useEffect(() => {
        if (isL3PrePopulated && !!nodeChildren) {
            const userContextObj = getUserContext();
            // Check if userContextObj is not null or undefined
            if (!!userContextObj) {
                const productFamily = userContextObj["ProductFamily"]?.toLowerCase();
                const l3 = "l3_surface_" + productFamily;
                const productFamilyArray = nodeChildren.filter(e => e.Id.toString().toLowerCase().includes(l3));
                if (!!productFamilyArray && productFamilyArray.length > 0) {
                    props.handleOnOptionSelected(productFamilyArray[0].Id);
                }
            }
        }
    }, [isL3PrePopulated, nodeChildren]);

    const getDropdownStyles = (themeName: string): Partial<IDropdownStyles> | null => {
        return (props.sourceApp === SourceApps.SurfaceApp || props.sourceApp === SourceApps.SurfaceApp2) ? {
            dropdownItem: {
                backgroundColor: themeName === 'darkgray' ? '#444444' : color.white,
            },
            caretDown: {
                color: themeName === 'darkgray' && '#828282',
            },
        } : null;
    };

    /**
     * find placeholder based on l1_surface_id
     */
    const getSurfacePlaceHolder = React.useMemo(() => {
        if (isFixed && !!nodeChildren) {
            const surfaceArray = nodeChildren.filter(e => e.Id.toString().toLowerCase().indexOf('l1_surface') > -1);
            if (!!surfaceArray && surfaceArray.length > 0) {
                return surfaceArray[0].Label;
            }
        }
    }, [isFixed, nodeChildren]);
    
    /**
     * find placeholder based on l3_surface_productid
     */
    const getSurfaceL3PlaceHolder = React.useMemo(() => {
        if (isL3PrePopulated && !!nodeChildren) {
            const userContextObj = getUserContext();
            // Check if userContextObj is not null or undefined
            if (!!userContextObj) {
                const productFamily = userContextObj["ProductFamily"]?.toLowerCase();
                const l3 = "l3_surface_" + productFamily;
                const productFamilyArray = nodeChildren.filter(e => e.Id.toString().toLowerCase().includes(l3));
                if (!!productFamilyArray && productFamilyArray.length > 0) {
                    return productFamilyArray[0].Label;
                }
            }
            // Handled the case if userContextObj or ProductFamily is not present in userContextObj then user 
            // will see the placeholder of dropdown as "Select a product family"
            return props.node.LevelLabel.Placeholder;
        }
    }, [isL3PrePopulated, nodeChildren]);

    const contactUsClassNames = getContactUsClassNames(getContactUsStyles, { theme: props.theme });
    return (
        <div className="form-group" id={`theme-routing-dropdown-group-${props.parentId}`} >
            <label
                htmlFor={`theme-routing-dropdown-item-${props.parentId}`}
                style={{ fontSize: '14px', fontWeight: 'normal', marginBottom: '0', lineHeight: '2' }}
            >{props.node.LevelLabel.Label}</label>
            <div className={mergeStyles('col-xs-12', contactUsClassNames.col, contactUsClassNames.row)} >
                <Dropdown
                    disabled={isFixed}
                    id={`theme-routing-dropdown-item-${props.parentId}`}
                    onChange={getDropdownValueFromEventTarget}
                    className={mergeStyles('form-control', contactUsClassNames.alchemyThemeDropdown)}
                    placeholder={isFixed ? getSurfacePlaceHolder : (isL3PrePopulated ? getSurfaceL3PlaceHolder : props.node.LevelLabel.Placeholder)}
                    options={dropdownOptions}
                    styles={isGetHelpSourceApp(props.sourceApp) ? getGetHelpDropdownStyles(props.theme): getDropdownStyles(props.themeName)}
                    aria-label={props.node.LevelLabel.Label}
                />
            </div>
        </div>
    );
};

const findNodeById = (id: string, nodesMap: ThemeRoutingNodesMap): AlchemyThemeRoutingNode | null => {
    return nodesMap[id] || null;
};
